import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle2, FileEdit, Plus, ShieldCheck } from 'lucide-react';
import { api } from '../api';
import type { Party, RateDriftItem, WeeklyBatch } from '../api';
import { AppShell, useReadOnly } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { ActionQueueCard, BatchTicket, MetricStrip } from '../ui/mobileOps';
import { Btn, EmptyState, RowSkeleton, SectionCard } from '../ui/components';
import { formatRatePercent } from '../batch/ReopenFixWizard';

type BatchFilter = 'all' | 'draft' | 'verified' | 'archived';

const FILTERS: ReadonlyArray<{ value: BatchFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'verified', label: 'Verified' },
  { value: 'archived', label: 'Posted' },
];

const STATE_WEIGHT: Record<WeeklyBatch['state'], number> = {
  draft: 0,
  verified: 1,
  archived: 2,
};

export function BatchesPage() {
  const nav = useNavigate();
  const readOnly = useReadOnly();
  const [filter, setFilter] = useState<BatchFilter>('all');

  const batchesQ = useQuery<WeeklyBatch[]>({
    queryKey: ['batches'],
    queryFn: () => api.get<WeeklyBatch[]>('/api/batches'),
  });
  const uplinesQ = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });
  // Rate-drift detector (Phase 3c) — posted weeks whose snapshotted rates no
  // longer match the assignment history. Repair path: Reopen & Fix, oldest
  // week first (the API already returns them oldest-first).
  const rateDriftQ = useQuery<RateDriftItem[]>({
    queryKey: ['rate-drift'],
    queryFn: () => api.get<RateDriftItem[]>('/api/rate-drift'),
    staleTime: 60_000,
  });
  const drift = rateDriftQ.data ?? [];
  const driftWeeks = new Set(drift.map((d) => d.batchId)).size;
  const oldestDrift = drift[0];

  const uplineById = useMemo(
    () => new Map((uplinesQ.data ?? []).map((party) => [party.id, party.displayName])),
    [uplinesQ.data],
  );

  const counts = useMemo(() => {
    const list = batchesQ.data ?? [];
    return {
      draft: list.filter((batch) => batch.state === 'draft').length,
      verified: list.filter((batch) => batch.state === 'verified').length,
      archived: list.filter((batch) => batch.state === 'archived').length,
    };
  }, [batchesQ.data]);

  const filtered = useMemo(() => {
    const list = [...(batchesQ.data ?? [])].sort((a, b) => {
      const stateOrder = STATE_WEIGHT[a.state] - STATE_WEIGHT[b.state];
      if (stateOrder !== 0) return stateOrder;
      return b.createdAt.localeCompare(a.createdAt);
    });
    if (filter === 'all') return list;
    return list.filter((batch) => batch.state === filter);
  }, [batchesQ.data, filter]);

  const queueTitle =
    counts.draft > 0
      ? `${counts.draft} draft batch${counts.draft === 1 ? '' : 'es'} need review`
      : counts.verified > 0
      ? `${counts.verified} verified batch${counts.verified === 1 ? '' : 'es'} ready to post`
      : 'Posting queue is clear';

  const queueTone = counts.draft > 0 ? 'warn' : counts.verified > 0 ? 'accent' : 'neutral';

  return (
    <AppShell
      title="Weekly batches"
      toolbar={
        <PageHeader
          section="batches"
          title="Weekly batches"
          subtitle="Review, verify, and post weekly sheets"
          actions={
            !readOnly ? (
              <Btn size="sm" onClick={() => nav('/batches/new')}>
                <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                <span>New batch</span>
              </Btn>
            ) : undefined
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        {oldestDrift && (
          <div className="rounded-card border border-amber-300 bg-amber-50 p-4">
            <h3 className="text-small font-semibold text-amber-700">
              Rates look off in {driftWeeks} posted week{driftWeeks === 1 ? '' : 's'}
            </h3>
            <p className="mt-1 text-small text-amber-700">
              Oldest first: <strong>{oldestDrift.accountName}</strong> in the week of{' '}
              {oldestDrift.weekMonday} was posted at{' '}
              {formatRatePercent(oldestDrift.uplineRateWas)}, but the rate on file is
              now {formatRatePercent(oldestDrift.uplineRateNow)}. Reopen that week to
              repost it with the correct rate, then come back for the next one.
            </p>
            {!readOnly && (
              <div className="mt-2">
                <Btn
                  size="sm"
                  onClick={() => nav(`/batches/${oldestDrift.batchId}?fix=1`)}
                >
                  Fix the oldest week
                </Btn>
              </div>
            )}
          </div>
        )}
        <div className="order-2 md:order-1">
          {!batchesQ.isLoading && (
            <MetricStrip
            section="batches"
            items={[
              { label: 'Draft', value: String(counts.draft), note: 'needs review', tone: counts.draft > 0 ? 'warn' : 'default' },
              { label: 'Verified', value: String(counts.verified), note: 'ready to post', tone: counts.verified > 0 ? 'pos' : 'default' },
              { label: 'Posted', value: String(counts.archived), note: 'archived work' },
            ]}
            />
          )}
        </div>

        <div className="order-1 md:order-2">
          <ActionQueueCard
          section="batches"
          eyebrow="Queue"
          title={queueTitle}
          description="Filter the queue and open the next sheet without losing context."
          tone={queueTone}
          actions={
            <>
              <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((item) => {
                  const active = filter === item.value;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setFilter(item.value)}
                      className="rounded-full border px-3 py-1.5 text-small font-medium transition-colors"
                      style={
                        active
                          ? { background: 'var(--sec-batches)', color: '#fff', borderColor: 'var(--sec-batches)' }
                          : { background: '#fff', color: 'var(--ink-muted)', borderColor: 'var(--border)' }
                      }
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
              {!readOnly && (
                <Btn size="sm" variant="secondary" onClick={() => nav('/batches/new')}>
                  Create batch
                </Btn>
              )}
            </>
          }
        />

        </div>

        <SectionCard section="batches" title="Batch work queue">
          {batchesQ.isLoading ? (
            <RowSkeleton rows={6} />
          ) : filtered.length === 0 ? (
            <EmptyState
              section="batches"
              icon={Calendar}
              title={filter === 'all' ? 'No batches yet' : `No ${filter} batches`}
              description={
                filter === 'all'
                  ? 'Create your first weekly batch when the upline P&L sheet comes in.'
                  : 'This filter is clear right now.'
              }
              action={
                !readOnly && filter === 'all' ? (
                  <Btn size="sm" onClick={() => nav('/batches/new')}>
                    New batch
                  </Btn>
                ) : undefined
              }
            />
          ) : (
            <div className="space-y-3">
              {filtered.map((batch) => {
                const stateSummary =
                  batch.state === 'draft'
                    ? 'Review settlements and warnings'
                    : batch.state === 'verified'
                    ? 'Ready for posting'
                    : 'Posted to ledgers';
                return (
                  <BatchTicket
                    key={batch.id}
                    to={`/batches/${batch.id}`}
                    week={batch.weekMonday}
                    title={uplineById.get(batch.uplinePartyId) ?? `Party #${batch.uplinePartyId}`}
                    state={batch.state}
                    detail={`Batch #${batch.id}`}
                    note={stateSummary}
                    footer={
                      <span className="inline-flex items-center gap-1">
                        {batch.state === 'draft' ? (
                          <FileEdit className="h-3.5 w-3.5" strokeWidth={2} />
                        ) : batch.state === 'verified' ? (
                          <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
                        )}
                        Created {batch.createdAt.slice(0, 10)}
                      </span>
                    }
                  />
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>
    </AppShell>
  );
}
